'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import Immutable from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import { ICON_SIZES } from 'UIComponents/icon/IconConstants';
import UIFileButton from 'UIComponents/button/UIFileButton';
import UICloseButton from 'UIComponents/button/UICloseButton';
import { EERIE, CALYPSO } from 'HubStyleTokens/colors';
import UIDropZone from 'UIComponents/drop/UIDropZone';
import Small from 'UIComponents/elements/Small';
import { uploadFileV3 } from 'FileManagerCore/api/Files';
import { FileUploadOptionsPropType } from 'FileManagerCore/constants/propTypes';
import { pickValidUploadV3Options } from '../utils/fileUploads';
import { logNewRelicPageAction, serializeFileInfo } from 'FileManagerCore/utils/logging';
import { getUploadErrorType } from 'FileManagerCore/utils/errors';
import { getIsDropZoneNoBrowseFileAccessValid } from '../utils/validateFileAccess';
import { UploadStatus } from '../Constants';
import DropTargetZone from './DropTargetZone';
import FileUploadComponentSize from '../enums/FileUploadComponentSize';
import { DropZoneNoBrowseFileAccess } from '../enums/FileAccess';
import DropZoneIconSize from '../enums/DropZoneIconSize';
import { getFileDataUploadStatus } from '../utils/getFileDataUploadStatus';
import FileExtensionFilters from '../enums/FileExtensionFilters';
import DropZoneErrorTypes from '../enums/DropZoneErrorTypes';
import { isExtensionFromRawFileSupported, getFileExtensionFromRawFile, buildFilterFromPOJOs } from '../utils/fileFiltering';
import FileDropZoneUploadError from './errors/FileDropZoneUploadError';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class FileUploadComponent extends Component {
  constructor(props) {
    super(props);
    this.setXhr = xhr => {
      this._xhr = xhr;
    };
    this.abortCurrentRequest = () => {
      const {
        onChange
      } = this.props;
      if (this._xhr && this._xhr.readyState !== 4) {
        this._xhr.abort();
        this._xhr = null;
      }
      this.setState({
        progress: null,
        status: null,
        fileInfo: null
      });
      onChange(null);
    };
    this.handleRemove = () => {
      const {
        onClose
      } = this.props;
      this.abortCurrentRequest();
      if (onClose) {
        onClose();
      }
    };
    this.handleProgress = event => {
      const progress = event.loaded / event.total * 100;
      this.setState({
        progress
      });
    };
    this.handleUploadClick = event => {
      this.handleUpload(event.target.files);
    };
    this.handleUpload = (files, {
      target = 'upload-button'
    } = {}) => {
      this.setState({
        error: null
      });
      if (!files || files.length === 0) {
        return;
      }
      const file = files[0];
      const {
        _httpClient,
        onChange,
        fileUploadOptions,
        onFileAdd,
        filterType,
        filteredExtensions,
        filteredReasons,
        uploadedFileAccess
      } = this.props;
      this.abortCurrentRequest();
      const filter = buildFilterFromPOJOs(filterType, filteredExtensions, filteredReasons);
      if (filter.get('filterType') !== FileExtensionFilters.NONE && !isExtensionFromRawFileSupported(file, filter)) {
        this.setState({
          error: {
            type: DropZoneErrorTypes.INVALID_FILE_EXTENSION,
            fileExtension: getFileExtensionFromRawFile(file),
            filter
          }
        });
        return;
      }
      this.setState({
        progress: 0,
        status: UploadStatus.PROCESSING,
        fileInfo: file
      });
      if (typeof onFileAdd === 'function') {
        onFileAdd(file);
      }
      uploadFileV3(file, uploadedFileAccess, pickValidUploadV3Options(fileUploadOptions), {
        updateProgress: this.handleProgress,
        xhrCallback: this.setXhr,
        httpClient: _httpClient
      }).then(data => {
        const fileData = data.getIn(['objects', 0]);
        const url = fileData.get('url');
        this.setState({
          progress: null,
          status: UploadStatus.FINISHED
        });
        onChange(url, fileData.set('file', file), {
          target
        });
        this.logPageAction('uploadSuccess', Object.assign({}, serializeFileInfo(file), {
          target
        }));
      }).catch(err => {
        const errorType = getUploadErrorType(err);
        this.logPageAction('uploadFailed', Object.assign({}, serializeFileInfo(file), {
          errorType,
          target
        }));
        if (this.state.fileInfo === file) {
          this.setState({
            progress: null,
            status: UploadStatus.FAILED,
            error: {
              type: errorType
            }
          });
          const errorData = Immutable.fromJS({
            file,
            data: err.data,
            message: err.message,
            responseJSON: err.responseJSON,
            responseText: err.responseText,
            status: err.status,
            statusText: err.statusText
          });
          onChange(null, errorData);
        }
      });
    };
    this.state = {
      progress: null,
      status: getFileDataUploadStatus(props.fileData),
      fileInfo: props.fileData ? props.fileData.toObject() : null
    };
    this._xhr = null;
    if (!getIsDropZoneNoBrowseFileAccessValid(props.uploadedFileAccess)) {
      throw new Error('[FileManagerLib/FileUploadComponent] Missing uploadedFileAccess prop. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
    }
  }
  componentDidMount() {
    this.logPageAction('mount', {
      componentName: this.constructor.displayName,
      uploadedFileAccess: this.props.uploadedFileAccess,
      isFiltering: Boolean(this.props.filterType)
    });
  }
  componentDidUpdate(prevProps) {
    if (this.props.fileData !== prevProps.fileData) {
      this.setState({
        status: getFileDataUploadStatus(this.props.fileData),
        fileInfo: this.props.fileData ? this.props.fileData.toObject() : null
      });
    }
  }
  logPageAction(eventName, extra = {}) {
    logNewRelicPageAction(eventName, Object.assign({}, extra, {
      componentName: this.constructor.displayName,
      uploadedFileAccess: this.props.uploadedFileAccess
    }));
  }
  shouldRenderNanoProgress() {
    const {
      showNanoProgress
    } = this.props;
    const {
      status
    } = this.state;
    return showNanoProgress && status === UploadStatus.PROCESSING;
  }
  shouldRenderSpinnerProgress() {
    const {
      showSpinnerProgress
    } = this.props;
    const {
      status
    } = this.state;
    return showSpinnerProgress && status === UploadStatus.PROCESSING;
  }
  shouldRenderFileData() {
    const {
      fileInfo
    } = this.state;
    const {
      showFileData
    } = this.props;
    return showFileData && Boolean(fileInfo) && this.getStatus() !== 'error';
  }
  getIconSize() {
    const {
      iconSize,
      size
    } = this.props;
    return iconSize || DropZoneIconSize[size];
  }
  getStatus() {
    const {
      status,
      error
    } = this.state;
    if (status === UploadStatus.PROCESSING) {
      return 'processing';
    }
    if (status === UploadStatus.FAILED || error) {
      return 'error';
    }
    if (status === UploadStatus.FINISHED) {
      return this.props.status || 'success';
    }
    return this.props.status || 'default';
  }
  showIcon() {
    if (this.getStatus() === 'processing') {
      return false;
    }
    return true;
  }
  getIconName() {
    if (this.state.error) {
      return 'warning';
    }
    return this.props.iconName;
  }
  renderUploadButton() {
    const {
      uploadButtonRenderer
    } = this.props;
    if (uploadButtonRenderer) {
      return uploadButtonRenderer(this.handleUploadClick);
    }
    return /*#__PURE__*/_jsx(UIFileButton, {
      size: "small",
      onChange: this.handleUploadClick,
      "data-test-id": "file-upload-button",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "FileManagerCore.actions.upload"
      })
    });
  }
  renderActions() {
    const {
      children
    } = this.props;
    const {
      status
    } = this.state;
    if (children) {
      return children;
    }
    if (status === UploadStatus.PROCESSING) {
      return null;
    }
    return this.renderUploadButton();
  }
  renderSpinnerProgress(size) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      className: "align-self-center",
      size: size,
      use: "success"
    });
  }
  renderFileNanoProgress() {
    const {
      progress
    } = this.state;
    return /*#__PURE__*/_jsx(UINanoProgress, {
      value: progress,
      boxShadow: false,
      color: CALYPSO
    });
  }
  renderFileName() {
    const {
      fileInfo
    } = this.state;
    const {
      fileData
    } = this.props;
    const fileSize = fileInfo && fileInfo.size;
    if (this.getStatus() === 'error' && typeof fileSize !== 'number') {
      return null;
    }
    if (fileInfo) {
      const originalFileName = fileInfo.file ? fileInfo.file.name : fileInfo.name;
      const uploadedFileName = fileData ? `${fileData.get('name')}.${fileData.get('extension')}` : originalFileName;
      if (uploadedFileName.toLowerCase() !== originalFileName.toLowerCase()) {
        return /*#__PURE__*/_jsx("div", {
          className: "is--heading-7 m-bottom-0",
          "data-test-id": "uploaded-file-name",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "FileManagerLib.fileUploadRenamed",
            options: {
              fileName: uploadedFileName
            }
          })
        });
      }
      return /*#__PURE__*/_jsx("div", {
        className: "is--heading-7 m-bottom-0",
        "data-test-id": "uploaded-file-name",
        children: originalFileName
      });
    }
    return null;
  }
  renderFileData() {
    const {
      fileInfo,
      progress,
      status
    } = this.state;
    const fileSize = fileInfo && fileInfo.size;
    if (!fileInfo || !fileSize) {
      return null;
    }
    if (status === UploadStatus.PROCESSING) {
      const progressString = I18n.formatSize(fileSize * (progress / 100)).toUpperCase();
      const totalString = I18n.formatSize(fileInfo.size).toUpperCase();
      return /*#__PURE__*/_jsx(Small, {
        style: {
          minWidth: 120
        },
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "FileManagerLib.percentDownloadOf",
          options: {
            part: progressString,
            total: totalString
          }
        })
      });
    }
    return /*#__PURE__*/_jsx(Small, {
      children: I18n.formatSize(fileSize).toUpperCase()
    });
  }
  renderCloseButton() {
    const {
      fileInfo
    } = this.state;
    const {
      closeable
    } = this.props;
    if (fileInfo && closeable) {
      return /*#__PURE__*/_jsx(UICloseButton, {
        onClick: this.handleRemove,
        color: EERIE,
        size: "small"
      });
    }
    return null;
  }
  renderSmallDropZone() {
    const {
      size,
      showFileName
    } = this.props;
    const {
      error
    } = this.state;
    return /*#__PURE__*/_jsxs(DropTargetZone, {
      className: `file-upload-zone status-${this.getStatus()}`,
      iconName: this.showIcon() ? this.getIconName() : null,
      iconSize: this.getIconSize(),
      status: this.getStatus(),
      size: size,
      onRemove: this.handleRemove,
      onUpload: this.handleUpload,
      children: [this.shouldRenderNanoProgress() && this.renderFileNanoProgress(), /*#__PURE__*/_jsxs("div", {
        className: "justify-between",
        children: [this.shouldRenderSpinnerProgress() && this.renderSpinnerProgress('extra-small'), /*#__PURE__*/_jsxs("div", {
          className: "flex-column text-center",
          children: [showFileName && this.renderFileName(), this.shouldRenderFileData() && this.renderFileData(), this.renderActions(), error && /*#__PURE__*/_jsx(FileDropZoneUploadError, Object.assign({}, error))]
        })]
      }), this.renderCloseButton()]
    });
  }
  renderBigDropZone() {
    const {
      size,
      showFileName
    } = this.props;
    const {
      error
    } = this.state;
    return /*#__PURE__*/_jsxs(DropTargetZone, {
      className: `file-upload-zone status-${this.getStatus()}`,
      iconName: this.showIcon() ? this.getIconName() : null,
      status: this.getStatus(),
      size: size,
      onRemove: this.handleRemove,
      onUpload: this.handleUpload,
      children: [this.shouldRenderNanoProgress() && this.renderFileNanoProgress(), this.shouldRenderSpinnerProgress() && this.renderSpinnerProgress('small'), showFileName && this.renderFileName(), error && /*#__PURE__*/_jsx(FileDropZoneUploadError, Object.assign({}, error)), this.shouldRenderFileData() && this.renderFileData(), this.renderActions(), this.renderCloseButton()]
    });
  }
  render() {
    const {
      size
    } = this.props;
    if (size === FileUploadComponentSize.small) {
      return this.renderSmallDropZone();
    }
    return this.renderBigDropZone();
  }
}
FileUploadComponent.displayName = 'FileUploadComponent';
FileUploadComponent.propTypes = {
  children: PropTypes.node,
  uploadButtonRenderer: PropTypes.func,
  iconName: PropTypes.string,
  iconSize: PropTypes.oneOf(Object.keys(ICON_SIZES)),
  size: PropTypes.oneOf(Object.keys(FileUploadComponentSize)).isRequired,
  _httpClient: PropTypes.func,
  fileData: PropTypes.instanceOf(Immutable.Map),
  onChange: PropTypes.func.isRequired,
  status: UIDropZone.propTypes.use,
  closeable: PropTypes.bool,
  showFileData: PropTypes.bool,
  showFileName: PropTypes.bool,
  onClose: PropTypes.func,
  fileUploadOptions: FileUploadOptionsPropType,
  uploadedFileAccess: PropTypes.oneOf(Object.keys(DropZoneNoBrowseFileAccess)),
  showSpinnerProgress: PropTypes.bool,
  showNanoProgress: PropTypes.bool,
  onFileAdd: PropTypes.func,
  filteredExtensions: PropTypes.arrayOf(PropTypes.string),
  filteredReasons: PropTypes.object,
  filterType: PropTypes.oneOf(Object.keys(FileExtensionFilters))
};
FileUploadComponent.defaultProps = {
  iconName: null,
  size: FileUploadComponentSize.large,
  iconSize: DropZoneIconSize.large,
  showSpinnerProgress: true,
  showNanoProgress: true,
  showFileData: true,
  showFileName: true,
  status: UIDropZone.defaultProps.use
};